import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Footer from "../Components/Footer";

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

function Section({ title, children }) {
  return (
    <div className="settings-section">
      <h3 className="settings-section-title">{title}</h3>
      {children}
    </div>
  );
}

function GoogleSection({ token }) {
  const loc      = useLocation();
  const navigate = useNavigate();
  const params   = new URLSearchParams(loc.search);
  const googleParam = params.get("google");

  const [status,       setStatus]       = useState(null);
  const [statusLoad,   setStatusLoad]   = useState(true);
  const [locations,    setLocations]    = useState([]);
  const [locLoad,      setLocLoad]      = useState(false);
  const [connecting,   setConnecting]   = useState(false);
  const [disconnecting,setDisconnecting]= useState(false);
  const [msg,          setMsg]          = useState({ text: "", ok: false });

  useEffect(() => {
    fetch(`${API}/auth/google/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => { setStatus(d); setStatusLoad(false); })
      .catch(() => setStatusLoad(false));
  }, [token]);

  useEffect(() => {
    if (googleParam === "connected" && token) loadLocations();
  }, [googleParam, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLocations = () => {
    setLocLoad(true);
    setMsg({ text: "", ok: false });
    fetch(`${API}/google/locations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.locations) setLocations(d.locations);
        else setMsg({ text: d.detail || "No business locations found.", ok: false });
        setLocLoad(false);
      })
      .catch(() => { setMsg({ text: "Failed to load locations.", ok: false }); setLocLoad(false); });
  };

  const handleConnect = () => {
    setConnecting(true);
    fetch(`${API}/auth/google/authorize`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.url) { window.location.href = d.url; }
        else { setMsg({ text: "Failed to get Google authorization URL.", ok: false }); setConnecting(false); }
      })
      .catch(() => { setMsg({ text: "Failed to connect to Google.", ok: false }); setConnecting(false); });
  };

  const handleSelectLocation = (loc) => {
    setLocLoad(true);
    fetch(`${API}/google/select-location`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ account_id: loc.account_id, location_id: loc.location_id, location_name: loc.name }),
    })
      .then((r) => r.json())
      .then(() => {
        setLocLoad(false);
        navigate("/dashboard");
      })
      .catch(() => { setMsg({ text: "Failed to select location.", ok: false }); setLocLoad(false); });
  };

  const handleDisconnect = () => {
    setDisconnecting(true);
    fetch(`${API}/auth/google/disconnect`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(() => {
        setStatus({ connected: false, location_set: false, location_name: null });
        setLocations([]);
        setMsg({ text: "", ok: false });
        setDisconnecting(false);
      })
      .catch(() => setDisconnecting(false));
  };

  if (statusLoad) {
    return <div className="settings-google-loading">Checking connection status...</div>;
  }

  const showLocationPicker = (googleParam === "connected" || (status?.connected && !status?.location_set)) && !msg.ok;

  return (
    <div className="settings-google-wrap">
      {googleParam === "error" && (
        <div className="settings-msg err">Google authorization failed. Please try again.</div>
      )}
      {msg.text && (
        <div className={`settings-msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</div>
      )}

      {!status?.connected ? (
        <div className="settings-google-block">
          <p className="settings-google-desc">
            Connect your Google Business Profile to pull all your reviews into RIRA and post replies directly to Google — without leaving the dashboard.
          </p>
          <button className="settings-google-btn" onClick={handleConnect} disabled={connecting}>
            {connecting ? "Redirecting to Google..." : "Connect Google Account"}
          </button>
        </div>
      ) : showLocationPicker ? (
        <div className="settings-google-block">
          <p className="settings-google-desc">Google account connected. Select the business location to manage:</p>
          {locLoad ? (
            <div className="settings-google-loading">Loading your business locations...</div>
          ) : locations.length > 0 ? (
            <div className="settings-location-list">
              {locations.map((loc) => (
                <button
                  key={`${loc.account_id}-${loc.location_id}`}
                  className="settings-location-item"
                  onClick={() => handleSelectLocation(loc)}
                >
                  <span className="settings-location-name">{loc.name}</span>
                  {loc.address && <span className="settings-location-addr">{loc.address}</span>}
                </button>
              ))}
            </div>
          ) : (
            <button className="settings-google-btn" onClick={loadLocations}>
              Load Business Locations
            </button>
          )}
        </div>
      ) : (
        <div className="settings-google-block settings-google-block--connected">
          <div className="settings-google-status-row">
            <span className="settings-sub-dot active" />
            <span className="settings-google-biz-name">{status.location_name}</span>
          </div>
          <p className="settings-google-desc">
            Connected. When you search for "{status.location_name}" in the dashboard, reviews will be fetched from Google Business Profile and you can post replies directly to Google.
          </p>
          <button className="settings-google-disconnect" onClick={handleDisconnect} disabled={disconnecting}>
            {disconnecting ? "Disconnecting..." : "Disconnect Google Account"}
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationsSection({ token, userEmail }) {
  const [alerts,   setAlerts]   = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState("");

  useEffect(() => {
    fetch(`${API}/settings/alerts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { setAlerts(d.email_alerts); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const toggle = () => {
    const next = !alerts;
    setAlerts(next);
    setSaving(true);
    fetch(`${API}/settings/alerts`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ email_alerts: next }),
    })
      .then(() => { setSaving(false); setMsg(next ? "Alerts enabled." : "Alerts disabled."); setTimeout(() => setMsg(""), 2500); })
      .catch(() => { setAlerts(!next); setSaving(false); });
  };

  if (loading) return <p className="settings-google-loading">Loading...</p>;

  return (
    <div className="settings-notif-wrap">
      <div className="settings-notif-row">
        <div>
          <div className="settings-notif-title">Negative review email alerts</div>
          <div className="settings-notif-desc">
            Get an email at <strong>{userEmail}</strong> whenever a 1–2★ review comes in, with the review text and a link to reply.
          </div>
        </div>
        <button
          className={`settings-toggle ${alerts ? "on" : "off"}`}
          onClick={toggle}
          disabled={saving}
          title={alerts ? "Disable alerts" : "Enable alerts"}
        >
          <span className="settings-toggle-knob" />
        </button>
      </div>
      <div className="settings-notif-row">
        <div>
          <div className="settings-notif-title">Weekly digest email</div>
          <div className="settings-notif-desc">
            Every Monday morning: avg rating, positive %, response rate, and a link to your dashboard.
          </div>
        </div>
        <button
          className={`settings-toggle ${alerts ? "on" : "off"}`}
          onClick={toggle}
          disabled={saving}
          title={alerts ? "Disable digest" : "Enable digest"}
        >
          <span className="settings-toggle-knob" />
        </button>
      </div>
      {msg && <p className="settings-msg ok">{msg}</p>}
      <p className="settings-notif-note">Emails require SMTP to be configured in the backend .env file.</p>
    </div>
  );
}

function SettingsPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  const [oldPw,  setOldPw]  = useState("");
  const [newPw,  setNewPw]  = useState("");
  const [confPw, setConfPw] = useState("");
  const [pwMsg,  setPwMsg]  = useState({ text: "", ok: false });
  const [pwLoad, setPwLoad] = useState(false);

  const isSubscribed = user?.subscription_expires && new Date(user.subscription_expires) > new Date();
  const expiry = user?.subscription_expires
    ? new Date(user.subscription_expires).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ text: "", ok: false });
    if (newPw !== confPw) { setPwMsg({ text: "New passwords do not match.", ok: false }); return; }
    if (newPw.length < 6) { setPwMsg({ text: "Password must be at least 6 characters.", ok: false }); return; }
    setPwLoad(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ old_password: oldPw, new_password: newPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setPwMsg({ text: "Password updated successfully.", ok: true });
      setOldPw(""); setNewPw(""); setConfPw("");
    } catch (err) {
      setPwMsg({ text: err.message, ok: false });
    }
    setPwLoad(false);
  };

  return (
    <div className="settings-page">
      <header className="settings-header">
        <button className="settings-back" onClick={() => navigate("/dashboard")}>
          ← Dashboard
        </button>
        <span className="settings-header-title">Account Settings</span>
        <button className="settings-signout" onClick={() => { logout(); navigate("/"); }}>Sign out</button>
      </header>

      <div className="settings-body">

        {/* Profile */}
        <Section title="Profile">
          <div className="settings-profile-row">
            <div className="settings-field-group">
              <label>Full name</label>
              <div className="settings-static">{user?.name}</div>
            </div>
            <div className="settings-field-group">
              <label>Email</label>
              <div className="settings-static">{user?.email}</div>
            </div>
            <div className="settings-field-group">
              <label>Member since</label>
              <div className="settings-static">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "—"}
              </div>
            </div>
          </div>
        </Section>

        {/* Subscription */}
        <Section title="Subscription">
          <div className="settings-sub-card">
            <div className="settings-sub-left">
              <div className="settings-sub-status">
                <span className={`settings-sub-dot ${isSubscribed ? "active" : "inactive"}`} />
                <span className="settings-sub-label">{isSubscribed ? "Active" : "Inactive"}</span>
              </div>
              <div className="settings-sub-plan">
                {user?.plan && user.plan !== "none"
                  ? `${user.plan.charAt(0).toUpperCase() + user.plan.slice(1)} plan`
                  : "No active plan"}
              </div>
              {expiry && isSubscribed && (
                <div className="settings-sub-expiry">Renews {expiry}</div>
              )}
              {!isSubscribed && (
                <div className="settings-sub-expiry expired">Your subscription has expired.</div>
              )}
            </div>
            <div className="settings-sub-actions">
              {!isSubscribed && (
                <button className="settings-upgrade-btn" onClick={() => navigate("/pricing")}>
                  Renew subscription
                </button>
              )}
              {isSubscribed && user?.plan === "monthly" && (
                <button className="settings-upgrade-btn" onClick={() => navigate("/pricing")}>
                  Upgrade to Yearly
                </button>
              )}
            </div>
          </div>
        </Section>

        {/* Google Business Profile */}
        <Section title="Google Business Profile">
          <GoogleSection token={token} />
        </Section>

        {/* Notifications */}
        <Section title="Notifications">
          <NotificationsSection token={token} userEmail={user?.email} />
        </Section>

        {/* Change password */}
        <Section title="Change Password">
          <form className="settings-pw-form" onSubmit={handleChangePassword}>
            <div className="settings-field-group">
              <label>Current password</label>
              <input
                type="password"
                placeholder="••••••••"
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                required
              />
            </div>
            <div className="settings-field-group">
              <label>New password</label>
              <input
                type="password"
                placeholder="••••••••"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="settings-field-group">
              <label>Confirm new password</label>
              <input
                type="password"
                placeholder="••••••••"
                value={confPw}
                onChange={(e) => setConfPw(e.target.value)}
                required
              />
            </div>
            {pwMsg.text && (
              <div className={`settings-msg ${pwMsg.ok ? "ok" : "err"}`}>{pwMsg.text}</div>
            )}
            <button type="submit" className="settings-pw-btn" disabled={pwLoad}>
              {pwLoad ? "Updating..." : "Update Password"}
            </button>
          </form>
        </Section>

      </div>
      <Footer />
    </div>
  );
}

export default SettingsPage;
